import ArrowCircleDownRoundedIcon from "@mui/icons-material/ArrowCircleDownRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Grid2, IconButton, Typography } from "@mui/material";
import { marked } from "marked";
import React, { useEffect, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import Ellipse from "../../../assets/icons/Ellipse";
import CommonButton from "../../../component/Button";
import CommonTextField from "../../../component/Input";
import { ChatBotFooter, CustomDiv } from "../../chat-bot/style";
import { CommonGrid2 } from "../../common-components/StyledComponents";
import IcanioIco from "../../../assets/icons/IcanioIco";

const KlarityChatBot = ({
  chatBotData,
  handleSendMessage,
  isBottomVisible,
  setIsBottomVisible,
  handleChatBotChange,
}) => {
  const messagesEndRef = useRef(null);

  const handleIconClick = () => {
    handleSendMessage();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatBotData.message]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsBottomVisible(entry.isIntersecting);
      },
      { threshold: 1.0 }
    );

    if (messagesEndRef.current) {
      observer.observe(messagesEndRef.current);
    }

    return () => {
      if (messagesEndRef.current) {
        observer.unobserve(messagesEndRef.current);
      }
    };
  });

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    category,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const labelRadius = outerRadius + 30; // Increase this to add more space
    const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#000"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        <tspan fontWeight={400}>{`${category}: `}</tspan>
        <tspan fontWeight={700}>{value}</tspan>
      </text>
    );
  };

  const EllipsisTick = ({ x, y, payload }) => {
    const text =
      payload.value.length > 10
        ? payload.value.slice(0, 10) + "..."
        : payload.value;

    return (
      <text x={x - 15} y={y + 10} textAnchor="start" fontSize={11}>
        {text}
      </text>
    );
  };

  return (
    <CustomDiv>
      <CommonGrid2
        overflowY="scroll"
        margin={chatBotData.message.length === 0 ? "0" : "0px 0px 12px"}
        maxHeight="380px"
        scrollStyle={true}
        width={"100%"}
        paddingRight={"15px"}
        paddingLeft={"5px"}
      >
        {chatBotData.message.map((msg, index) => (
          <div
            key={index}
            style={{
              textAlign: msg.sender === "user" ? "right" : "left",
              margin: "1rem 0",
            }}
          >
            <Typography
              fontSize={"13px"}
              textAlign={"left"}
              style={{
                display: "inline-block",
                padding: "5px 10px",
                backgroundColor:
                  msg.sender === "user"
                    ? "#7F56D9"
                    : msg.sender === "error"
                    ? "red"
                    : "#fff",
                color:
                  msg.sender === "user" || msg.sender === "error"
                    ? "#fff"
                    : "#000",
                borderRadius:
                  msg.sender === "user" ? "8px 8px 1px 8px" : "8px 8px 8px 1px",
                maxWidth: msg.sender === "user" ? "80%" : "99%",
                wordBreak: "break-word",
                boxShadow: "1px 1px 5px 1px rgb(16 24 40 / 11%)",
              }}
            >
              <div
                className="markdown-content"
                dangerouslySetInnerHTML={{ __html: marked(msg.text) }}
                style={{ fontFamily: "Arial", fontSize: 14 }}
              />
              {msg?.visualizations && msg?.visualizations?.length === 0 && (
                <Typography fontSize={"14px"}>{msg.reasoning}</Typography>
              )}
              {msg?.visualizations &&
                msg?.visualizations?.length !== 0 &&
                msg?.visualizations.map((item) => {
                  if (item.chart_type === "bar") {
                    const barWidth = 60;
                    const chartWidth =
                      item.data.length > 10
                        ? `${item.data.length * barWidth}px`
                        : "100%";
                    if (item.data.length === 0) return null;
                    return (
                      <CustomDiv
                        style={{
                          overflowX: "auto",
                          overflowY: "hidden",
                          borderRadius: "8px",
                          boxShadow: "1px 1px 5px 1px rgb(16 24 40 / 11%)",
                          backgroundColor: "#fff",
                          margin: "10px",
                          padding: "10px",
                        }}
                      >
                        <CustomDiv width={chartWidth} height={"300px"}>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {item.title}
                          </Typography>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              width={500}
                              height={300}
                              data={item.data}
                              margin={{
                                top: 50,
                                right: 30,
                                left: 20,
                                bottom: 20,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                              />
                              <XAxis
                                dataKey="category"
                                interval={0}
                                tick={<EllipsisTick />}
                              />
                              <YAxis orientation="right" />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="val1"
                                fill="#7F56D9"
                                barSize={20}
                                name={item?.value_names[0]}
                              />
                              {item.data.some((d) => d.val2 !== undefined) && (
                                <Bar
                                  dataKey="val2"
                                  fill="#D4B6FF"
                                  barSize={20}
                                  name={item?.value_names[1]}
                                />
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        </CustomDiv>
                      </CustomDiv>
                    );
                  } else if (item.chart_type === "line") {
                    const barWidth = 90;
                    const chartWidth =
                      item.data.length > 10
                        ? `${item.data.length * barWidth}px`
                        : "100%";
                    if (item.data.length === 0) return null;
                    return (
                      <CustomDiv
                        style={{
                          overflowX: "auto",
                          overflowY: "hidden",
                          borderRadius: "8px",
                          boxShadow: "1px 1px 5px 1px rgb(16 24 40 / 11%)",
                          backgroundColor: "#fff",
                          margin: "10px",
                          padding: "10px",
                        }}
                      >
                        <CustomDiv width={chartWidth} height={"300px"}>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {item.title}
                          </Typography>
                          <ResponsiveContainer width={"100%"} height="100%">
                            <LineChart
                              data={item.data}
                              margin={{
                                top: 20,
                                right: 20,
                                left: 20,
                                bottom: 20,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                              />
                              <XAxis
                                dataKey="x"
                                name={item.x_axis_name}
                                label={{
                                  value: item.x_axis_name,
                                  position: "bottom",
                                  offset: 0,
                                }}
                                tick={<EllipsisTick />}
                                // tick={{
                                //   angle: -45,
                                //   textAnchor: "end",
                                //   fontSize: 9,
                                // }}
                                interval={0}
                              />
                              <YAxis
                                orientation="right"
                                name={item.y_axis_name}
                                strokeWidth={0}
                                label={{
                                  value: item.y_axis_name,
                                  position: "center",
                                  dx: 35,

                                  angle: -90,
                                }}
                              />
                              <Tooltip />
                              <Line
                                type="monotone"
                                dataKey="y"
                                stroke="#8884d8"
                                activeDot={{ r: 8 }}
                              />
                              <Legend
                                wrapperStyle={{
                                  bottom: 0,

                                  height: "45px",
                                  left: "0px",
                                }}
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="center"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </CustomDiv>
                      </CustomDiv>
                    );
                  } else if (item.chart_type === "pie") {
                    if (item.data.length === 0) return null;
                    return (
                      <CustomDiv
                        height="360px"
                        style={{
                          borderRadius: "8px",
                          boxShadow: "1px 1px 5px 1px rgb(16 24 40 / 11%)",
                          backgroundColor: "#fff",
                          margin: "10px",
                          padding: "10px",
                        }}
                      >
                        <CustomDiv height="300px">
                          <Typography
                            fontSize={"14px"}
                            fontWeight={600}
                            marginBottom={"1rem"}
                          >
                            {item.title}
                          </Typography>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                dataKey="value"
                                isAnimationActive={false}
                                data={item.data}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                labelLine={true}
                                fill="#8884d8"
                                label={renderCustomLabel}
                              >
                                {item.data.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color || "#8884d8"}
                                  />
                                ))}
                              </Pie>

                              <Tooltip />
                              <Legend
                                wrapperStyle={{
                                  bottom: 0,
                                }}
                                content={({ payload }) => {
                                  return (
                                    <ul
                                      style={{
                                        listStyle: "none",
                                        display: "flex",
                                        gap: "1rem",
                                        padding: 0,
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {payload?.map((entry, index) => (
                                        <li
                                          key={`item-${index}`}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.4rem",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: 10,
                                              height: 10,
                                              backgroundColor: entry.color,
                                            }}
                                          />
                                          <span>{entry?.payload?.value}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  );
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </CustomDiv>
                      </CustomDiv>
                    );
                  } else if (item.chart_type === "scatter") {
                    return (
                      <CustomDiv
                        height="360px"
                        style={{
                          borderRadius: "8px",
                          boxShadow: "1px 1px 5px 1px rgb(16 24 40 / 11%)",
                          backgroundColor: "#fff",
                          margin: "10px",
                          padding: "10px",
                        }}
                      >
                        <CustomDiv height="300px">
                          <Typography
                            fontSize={"14px"}
                            fontWeight={600}
                            marginBottom={"1rem"}
                          >
                            {item.title}
                          </Typography>
                          <ResponsiveContainer width="100%" height={"100%"}>
                            <ScatterChart
                              margin={{
                                top: 20,
                                right: 15,
                                bottom: 10,
                                left: 15,
                              }}
                            >
                              <CartesianGrid />
                              <XAxis
                                type="number"
                                dataKey="x"
                                name={item.x_axis_name}
                                label={{
                                  value: item.x_axis_name,
                                  position: "bottom",
                                  offset: 0,
                                }}
                                interval={0}
                              />
                              <YAxis
                                type="number"
                                dataKey="y"
                                name={item.y_axis_name}
                                orientation="right"
                                label={{
                                  value: item.y_axis_name,
                                  position: "center",
                                  dx: 35,
                                  angle: -90,
                                }}
                              />
                              <ZAxis
                                type="number"
                                dataKey="category"
                                name={item.y_axis_name}
                              />
                              <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                              <Legend
                                wrapperStyle={{
                                  bottom: -5,
                                  left: "0px",
                                }}
                              />
                              <Scatter
                                name={item.category_name}
                                data={item.data}
                                fill="#7F56D9"
                                shape="circle"
                              />
                            </ScatterChart>
                          </ResponsiveContainer>
                        </CustomDiv>
                      </CustomDiv>
                    );
                  }
                })}

              {/* <CustomMarkDown>
                {typeof msg.text === "string"
                  ? msg.text.trim()
                  : JSON.stringify(msg.text)}
              </CustomMarkDown> */}
            </Typography>
          </div>
        ))}

        {chatBotData.loader && (
          <CustomDiv position={"relative"}>
            <Ellipse />
          </CustomDiv>
        )}
        <div ref={messagesEndRef} />
      </CommonGrid2>
      {!isBottomVisible && (
        <CustomDiv position={"relative"}>
          <IconButton
            onClick={scrollToBottom}
            style={{
              color: "#7F56D9",
              position: "absolute",
              left: "46.25%",
              background: "#fff",
              borderRadius: "50%",
              top: "-3rem",
              zIndex: 999,
              padding: "0px",
            }}
          >
            <ArrowCircleDownRoundedIcon style={{ fontSize: "25px" }} />
          </IconButton>
        </CustomDiv>
      )}

      <Grid2 item size={{ xs: 12 }}>
        <CommonTextField
          border={"0.5px solid #EAECF0"}
          padding={15}
          placeholder={"Ask anything.."}
          bgColor={"#F7F8F9"}
          value={chatBotData.value}
          onChange={handleChatBotChange}
          disabled={chatBotData.loader || true}
          endComponent={
            <CommonButton
              onClick={() => handleIconClick()}
              fontWeight={600}
              padding={"8px 25px"}
              value={"Ask AI"}
              disabled={true}
              endIcon={<AutoAwesomeIcon />}
            />
          }
        />
      </Grid2>
      <CommonGrid2
        display={"flex"}
        gap={"10px"}
        justifyContent={"center"}
        alignItems={"center"}
        marginTop={"10px"}
      >
        <ChatBotFooter>Powered by</ChatBotFooter>
        <IcanioIco />
      </CommonGrid2>
    </CustomDiv>
  );
};
export default KlarityChatBot;
